import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import { useAction, useTrouble } from "../App";
import { api } from "../api/client";
import type {
  BotView,
  Choice,
  Field,
  OptionValues,
  TaskView,
} from "../api/types";
import { Apparatus, Empty, Tag } from "../components/Parts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cronInWords } from "../lib/cron";
import { now as rightNow } from "../lib/format";
import {
  defaults,
  kindName,
  pretty,
  readBoolean,
  readList,
  readNumber,
  readString,
  withValue,
} from "../lib/options";
import { scheduleWords, switched } from "../lib/tasks";
import {
  Field as UiField,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

/** A saved run, with its schedule if it has one. */
export function Tasks({ navigate }: { navigate: (to: string) => void }) {
  const [bots, setBots] = useState<BotView[]>([]);
  const [tasks, setTasks] = useState<TaskView[] | null>(null);
  const [editing, setEditing] = useState<TaskView | null>(null);
  const [reload, setReload] = useState(0);
  const report = useTrouble();

  useEffect(() => {
    let live = true;
    Promise.all([api.bots(), api.tasks()])
      .then(([theBots, theTasks]) => {
        if (!live) return;
        setBots(theBots);
        setTasks(theTasks);
      })
      .catch(report);
    return () => {
      live = false;
    };
  }, [reload, report]);

  if (!tasks) return <Empty>Reading what is scheduled.</Empty>;

  const bot = editing ? bots.find((it) => it.id === editing.botId) : undefined;

  if (editing && bot) {
    return (
      <TaskForm
        task={editing}
        bot={bot}
        bots={bots}
        onBot={(picked) =>
          setEditing({
            ...editing,
            botId: picked.id,
            options: defaults(picked.options),
          })
        }
        onCancel={() => setEditing(null)}
        onSaved={(andRun) => {
          setEditing(null);
          setReload((it) => it + 1);
          if (andRun) navigate("/");
        }}
      />
    );
  }

  const first = bots[0];

  return (
    <>
      <div className="flex items-center gap-2.5">
        <h1 className="font-serif text-title font-medium -tracking-[0.015em]">
          Tasks
        </h1>
        <span className="flex-1" />
        {first ? (
          <Button onClick={() => setEditing(blank(first))}>New task</Button>
        ) : null}
      </div>

      {tasks.length === 0 ? (
        <Empty
          action={
            first ? (
              <Button onClick={() => setEditing(blank(first))}>New task</Button>
            ) : undefined
          }
        >
          No tasks yet. A task is a bot plus its options plus, optionally, a
          schedule.
        </Empty>
      ) : (
        tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            botName={
              bots.find((it) => it.id === task.botId)?.name ?? task.botId
            }
            onEdit={() => setEditing(task)}
            onChanged={() => setReload((it) => it + 1)}
            onStarted={() => navigate("/")}
          />
        ))
      )}
    </>
  );
}

function blank(bot: BotView): TaskView {
  return { id: 0, botId: bot.id, name: "", options: defaults(bot.options) };
}

function TaskRow({
  task,
  botName,
  onEdit,
  onChanged,
  onStarted,
}: {
  task: TaskView;
  botName: string;
  onEdit: () => void;
  onChanged: () => void;
  onStarted: () => void;
}) {
  const [busy, act] = useAction();
  const sub = scheduleWords(task, rightNow());
  const paused = task.enabled === false;

  return (
    <Card>
      <CardContent className="flex items-center gap-2.5">
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <span className="text-heading font-medium">{task.name}</span>
            <Tag>{botName}</Tag>
            {task.cron ? <Tag>{task.cron}</Tag> : null}
            {task.cron && paused ? <Tag>paused</Tag> : null}
          </div>
          {sub ? (
            <span className="font-mono text-mono text-muted-foreground">
              {sub}
            </span>
          ) : null}
        </div>

        <Button
          disabled={busy}
          onClick={() =>
            act(async () => {
              await api.start({ taskId: task.id ?? 0 });
              onStarted();
            })
          }
        >
          {busy ? "Queueing" : "Run now"}
        </Button>
        {task.cron ? (
          <Button
            variant="outline"
            disabled={busy}
            onClick={() =>
              act(async () => {
                await api.updateTask(switched(task));
                onChanged();
              })
            }
          >
            {paused ? "Resume" : "Pause"}
          </Button>
        ) : null}
        <Button variant="outline" onClick={onEdit}>
          Edit
        </Button>
        <Button
          variant="ghost"
          disabled={busy}
          onClick={() =>
            act(async () => {
              await api.deleteTask(task.id ?? 0);
              onChanged();
            })
          }
        >
          Delete
        </Button>
      </CardContent>
    </Card>
  );
}

/**
 * A task's options, built entirely from its bot's schema.
 *
 * The dashboard knows none of these field names. Every control below is chosen by the kind the bot
 * declared, which is why a second bot costs nothing here.
 */
function TaskForm({
  task,
  bot,
  bots,
  onBot,
  onCancel,
  onSaved,
}: {
  task: TaskView;
  bot: BotView;
  bots: BotView[];
  onBot: (bot: BotView) => void;
  onCancel: () => void;
  onSaved: (andRun: boolean) => void;
}) {
  const [draft, setDraft] = useState<TaskView>(task);
  const [busy, act] = useAction();

  useEffect(() => setDraft(task), [task]);

  const set = (key: string, value: Parameters<typeof withValue>[2]) =>
    setDraft((it) => ({
      ...it,
      options: withValue(it.options ?? {}, key, value),
    }));

  const save = (andRun: boolean) =>
    act(async () => {
      const stored = draft.id
        ? await api.updateTask(draft)
        : await api.addTask(draft);
      // Surfaced, not swallowed: the Kotlin reported a failed save and quietly lost a failed start.
      if (andRun) await api.start({ taskId: stored.id ?? 0 });
      onSaved(andRun);
    });

  const reading = draft.cron ? cronInWords(draft.cron) : null;
  const stored = pretty(draft.options ?? {});

  return (
    <div className="flex items-start gap-8 max-sm:flex-col max-sm:gap-6">
      <div className="flex min-w-0 flex-1 flex-col gap-5.5">
        <h1 className="font-serif text-title font-medium -tracking-[0.015em]">
          {draft.id ? "Edit task" : "New task"} — {bot.name}
        </h1>
        <p className="m-0 max-w-[60ch] text-row text-pretty text-muted-foreground">
          Every field below comes from the bot's schema. The dashboard knows
          none of their names.
        </p>

        {bots.length > 1 && !draft.id ? (
          <Labelled label="Bot">
            <Segmented
              choices={bots.map((it) => ({ value: it.id, label: it.name }))}
              chosen={bot.id}
              onPick={(picked) => {
                const next = bots.find((it) => it.id === picked);
                if (next) onBot(next);
              }}
            />
          </Labelled>
        ) : null}

        <Labelled label="Name" htmlFor="task-name">
          <Input
            id="task-name"
            value={draft.name}
            placeholder="what to call it"
            onChange={(event) =>
              setDraft({ ...draft, name: event.target.value })
            }
          />
        </Labelled>

        {(bot.options.fields ?? []).map((field) => (
          <Labelled
            key={field.key}
            label={field.label}
            kind={kindName(field)}
            help={field.help ?? undefined}
            htmlFor={field.key}
          >
            <Control
              bot={bot}
              field={field}
              values={draft.options ?? {}}
              onChange={set}
            />
          </Labelled>
        ))}

        <Labelled
          label="Schedule"
          help="Five fields, or blank to run only by hand."
          htmlFor="task-cron"
        >
          <Input
            id="task-cron"
            value={draft.cron ?? ""}
            placeholder="0 3 * * *"
            onChange={(event) =>
              setDraft({ ...draft, cron: event.target.value })
            }
          />
          {reading ? (
            <FieldDescription className="font-mono">{reading}</FieldDescription>
          ) : null}
        </Labelled>

        <Separator />

        <div className="flex flex-wrap items-center gap-3">
          <Button disabled={busy} onClick={() => save(true)}>
            Save and run now
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => save(false)}>
            Save only
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>

      <div className="flex w-85 flex-none flex-col gap-2.5 max-sm:w-full">
        <Apparatus>Options as stored</Apparatus>
        {stored ? (
          <pre className="m-0 font-mono text-mono whitespace-pre-wrap text-muted-foreground">
            {stored}
          </pre>
        ) : (
          <p className="m-0 text-row text-muted-foreground">
            Nothing answered yet.
          </p>
        )}
        <p className="m-0 max-w-[60ch] text-row text-pretty text-muted-foreground">
          This is the runs.options payload — the same shape the terminal wizard
          builds.
        </p>
      </div>
    </div>
  );
}

/**
 * One field of the form.
 *
 * `htmlFor` points at the control's id, which is the field's own key: unique within a schema by
 * construction, since the options map is keyed by it. Nothing in this form had an id before.
 */
function Labelled({
  label,
  kind,
  help,
  htmlFor,
  children,
}: {
  label: string;
  kind?: string;
  help?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <UiField>
      <FieldLabel htmlFor={htmlFor} className="apparatus justify-start gap-3">
        {label}
        {kind ? (
          <span className="font-mono text-[10px] normal-case">{kind}</span>
        ) : null}
      </FieldLabel>
      {help ? <FieldDescription>{help}</FieldDescription> : null}
      {children}
    </UiField>
  );
}

/**
 * The control a field kind gets.
 *
 * The `: ReactElement` is load-bearing. Without it TypeScript infers `Element | undefined` and a
 * sixth field kind added to the Kotlin would compile clean here and render nothing at all; with it,
 * the same addition is a missing-return error. `defaultValue` and `kindName` get that guarantee from
 * their own return annotations, and this is the third of the three switches that has to stay
 * exhaustive. No `default:` clause: `noFallthroughCasesInSwitch` and this annotation already do the
 * job, and a default branch is one refactor away from swallowing a real case.
 */
function Control({
  bot,
  field,
  values,
  onChange,
}: {
  bot: BotView;
  field: Field;
  values: OptionValues;
  onChange: (key: string, value: Parameters<typeof withValue>[2]) => void;
}): ReactElement {
  switch (field.type) {
    case "switch":
      return (
        <Switch
          id={field.key}
          checked={readBoolean(values, field.key, field.default ?? false)}
          onCheckedChange={(on) => onChange(field.key, on)}
        />
      );
    case "text":
      return (
        <Input
          id={field.key}
          value={readString(values, field.key, field.default ?? "")}
          placeholder={field.placeholder ?? ""}
          onChange={(event) => onChange(field.key, event.target.value)}
        />
      );
    case "number": {
      const held = readNumber(values, field.key);
      return (
        <Input
          id={field.key}
          // Not type="number": that hands back "" for anything the browser dislikes, so "12.5" and
          // "abc" would both arrive as null by a different path than the check below.
          inputMode="numeric"
          value={held === null ? "" : String(held)}
          placeholder={field.placeholder ?? "no limit"}
          onChange={(event) => {
            const text = event.target.value.trim();
            onChange(
              field.key,
              text === "" ? null : /^\d+$/.test(text) ? Number(text) : null,
            );
          }}
        />
      );
    }
    case "pick":
      return (
        <Segmented
          id={field.key}
          choices={field.choices ?? []}
          chosen={readString(values, field.key, field.default)}
          onPick={(picked) => onChange(field.key, picked)}
        />
      );
    case "multipick":
      return (
        <Chips
          bot={bot}
          field={field}
          chosen={readList(values, field.key)}
          onChange={(next) => onChange(field.key, next)}
        />
      );
  }
}

/**
 * One of a few.
 *
 * The `if (picked)` is not defensive tidying. A single toggle group deselects: clicking the item
 * that is already on fires the change with an empty string, which the old hand-rolled control could
 * not do. Without the guard one click writes "" into the options, nothing type-errors because a
 * pick's default is a plain string, the task saves, and the bot is handed a value matching none of
 * its choices.
 */
function Segmented({
  id,
  choices,
  chosen,
  onPick,
}: {
  id?: string;
  choices: Choice[];
  chosen: string;
  onPick: (value: string) => void;
}) {
  return (
    <ToggleGroup
      id={id}
      type="single"
      variant="outline"
      value={chosen}
      onValueChange={(picked) => {
        if (picked) onPick(picked);
      }}
      className="flex w-fit max-w-full flex-wrap justify-start"
    >
      {choices.map((choice) => (
        <ToggleGroupItem
          key={choice.value}
          value={choice.value}
          title={choice.hint ?? undefined}
        >
          {choice.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function Chips({
  bot,
  field,
  chosen,
  onChange,
}: {
  bot: BotView;
  field: Field & { type: "multipick" };
  chosen: string[];
  onChange: (next: string[]) => void;
}) {
  const declared = field.choices ?? [];
  const [choices, setChoices] = useState<Choice[]>(declared);
  // Three states, not two. A failed lookup used to render as "the bot has no choices to offer yet",
  // which is what a genuinely empty answer says -- so the screen asserted something false about the
  // bot, and reading the sentence sent us looking for a bug in the bot rather than in the wiki.
  const [asking, setAsking] = useState(declared.length === 0);
  const [failed, setFailed] = useState(false);
  const report = useTrouble();

  useEffect(() => {
    if (declared.length > 0) return undefined;

    let live = true;
    api
      .choices(bot.id, field.key)
      .then((it) => {
        if (!live) return;
        setChoices(it);
        setFailed(false);
      })
      .catch((error) => {
        if (!live) return;
        setFailed(true);
        // Reported as well as shown: the banner carries the server's sentence, which says which
        // lookup failed and why, and the field says only that this one did.
        report(error);
      })
      .finally(() => {
        if (live) setAsking(false);
      });
    return () => {
      live = false;
    };
  }, [bot.id, field.key, declared.length, report]);

  if (asking) {
    return (
      <FieldDescription>Asking the bot what it can offer.</FieldDescription>
    );
  }

  if (failed) {
    return (
      <FieldError>
        The bot could not say what it can offer. Leave this empty and it means
        all of them.
      </FieldError>
    );
  }

  if (choices.length === 0) {
    return (
      <FieldDescription>The bot has no choices to offer yet.</FieldDescription>
    );
  }

  return (
    <ToggleGroup
      type="multiple"
      variant="outline"
      value={chosen}
      onValueChange={onChange}
      aria-label={field.label}
      className="flex w-full flex-wrap justify-start gap-1"
    >
      {choices.map((choice) => (
        <ToggleGroupItem
          key={choice.value}
          value={choice.value}
          title={choice.hint ?? undefined}
        >
          {choice.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
